import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  CircularProgress,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import { getGoogleCalendarOauthUrl, getGoogleCalendarStatus } from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  readAgendaGooglePromptDismissed,
  persistAgendaGooglePromptDismissed,
} from "../../utils/agendaGooglePromptStorage";

function unwrapStatus(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

export default function AgendaGoogleConnectPrompt({ compact = false }) {
  const { t } = useTranslation("teamAgenda");
  const [dismissed, setDismissed] = useState(() => readAgendaGooglePromptDismissed());
  const [connectError, setConnectError] = useState("");

  const { data: statusRaw, isLoading } = useQuery(
    ["google-calendar-status"],
    async () => {
      const r = await getGoogleCalendarStatus();
      return unwrapStatus(r) ?? {};
    },
    { staleTime: 60_000 }
  );

  const connected = Boolean(statusRaw?.connected);
  const requiresLocalUser = Boolean(statusRaw?.requiresLocalUser);

  const connectMutation = useMutation(
    async () => {
      setConnectError("");
      const r = await getGoogleCalendarOauthUrl();
      const url = r?.url || unwrapStatus(r)?.url;
      if (!url || typeof url !== "string") {
        throw new Error(t("googleConnectPrompt.errors.noUrl"));
      }
      window.location.assign(url);
    },
    {
      onError: (err) => {
        const code = err?.code || err?.response?.data?.code || err?.data?.code;
        if (code === "google_calendar_no_local_user") {
          setConnectError(t("googleConnectPrompt.errors.noLocalUser"));
          return;
        }
        setConnectError(getApiErrorMessage(err, t("googleConnectPrompt.errors.connectFailed")));
      },
    }
  );

  const handleDismiss = useCallback(() => {
    persistAgendaGooglePromptDismissed();
    setDismissed(true);
  }, []);

  if (dismissed || isLoading || connected) return null;

  return (
    <Box
      sx={{
        mx: compact ? 1 : 0,
        mt: compact ? 0 : 0,
        mb: compact ? 0.75 : 1,
        p: compact ? 1.25 : 1.5,
        borderRadius: 2,
        flexShrink: 0,
        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.08),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
        position: "relative",
      }}
    >
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label={t("googleConnectPrompt.dismiss")}
        sx={{ position: "absolute", top: 4, right: 4 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pr: 3 }}>
        <EventIcon color="primary" sx={{ mt: 0.25, fontSize: compact ? 20 : 22 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant={compact ? "subtitle2" : "subtitle1"} fontWeight={700} gutterBottom>
            {t("googleConnectPrompt.title")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1.5 }}>
            {requiresLocalUser ? t("googleConnectPrompt.requiresLocalUserBody") : t("googleConnectPrompt.body")}
          </Typography>
          {!compact && !requiresLocalUser ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, lineHeight: 1.45 }}>
              {t("googleConnectPrompt.dismissHint")}
            </Typography>
          ) : null}

          {connectError ? (
            <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
              {connectError}
            </Typography>
          ) : null}

          <Stack direction={compact ? "column" : "row"} spacing={1} alignItems={compact ? "stretch" : "center"}>
            {!requiresLocalUser ? (
              <Button
                variant="contained"
                size="small"
                disabled={connectMutation.isLoading}
                startIcon={
                  connectMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : <EventIcon />
                }
                onClick={() => connectMutation.mutate()}
                sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {t("googleConnectPrompt.connect")}
              </Button>
            ) : null}
            <Button
              component={RouterLink}
              to="/profile"
              size="small"
              variant={requiresLocalUser ? "contained" : "text"}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("googleConnectPrompt.openProfile")}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
